import { Box, Button, Icon, ThemeProvider } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddButton } from "~/components/Buttons";
import ModalDialog from "~/components/ModalDialog";
import { BodyText, BodyTextSmall, SubtitleText } from "~/components/Typographies";
import AppThemeContext from "~/providers/AppThemeContext";
import { type InstallableApp } from "~/pwa/installableApps";
import useInstallPrompt from "~/pwa/useInstallPrompt";
import { themeCreator } from "~/themes/pageThemes";

interface InstallAppListProps {
	targets: InstallableApp[];
}

const iosSteps: { icon: string; text: string }[] = [
	{ icon: "ios_share", text: "Tap the Share button in Safari's toolbar." },
	{ icon: "add_box", text: "Choose “Add to Home Screen”." },
	{ icon: "check_circle", text: "Tap “Add” to finish — the app appears on your home screen." }
];

const InstallAppList = ({ targets }: InstallAppListProps) => {
	const { t } = useTranslation("common");
	const { platform, promptInstall } = useInstallPrompt();
	const { themeType } = useContext(AppThemeContext);
	const [stepsApp, setStepsApp] = useState<InstallableApp | null>(null);
	const [isFallbackOpen, setIsFallbackOpen] = useState(false);

	const handleAndroidInstall = async (): Promise<void> => {
		const outcome = await promptInstall();
		if (outcome === "unavailable") setIsFallbackOpen(true);
	};

	const themedTargets = useMemo(
		() => targets.map((app) => ({ app, theme: themeCreator(app.key, themeType) })),
		[targets, themeType]
	);

	return (
		<Box>
			{themedTargets.map(({ app, theme }) => (
				<Box
					key={app.key}
					sx={{ alignItems: "center", display: "flex", gap: 2, justifyContent: "space-between", py: 1 }}
				>
					<Box sx={{ minWidth: 0 }}>
						<SubtitleText sx={{ fontWeight: 400, lineHeight: 1.75 }}>{app.shortName}</SubtitleText>
						<BodyTextSmall color="text.secondary">{t("pwaInstall.description")}</BodyTextSmall>
					</Box>
					<Box sx={{ flexShrink: 0 }}>
						<ThemeProvider theme={theme}>
							{platform === "ios" ? (
								<AddButton onClick={() => setStepsApp(app)} size="small" startIcon={<Icon>ios_share</Icon>}>
									Add to Home Screen
								</AddButton>
							) : (
								<Button
									color="primary"
									onClick={() => void handleAndroidInstall()}
									size="small"
									startIcon={<Icon>install_mobile</Icon>}
									variant="contained"
								>
									Install
								</Button>
							)}
						</ThemeProvider>
					</Box>
				</Box>
			))}
			<ModalDialog
				close={() => setStepsApp(null)}
				open={stepsApp !== null}
				showCloseButton
				title={stepsApp ? `Install ${stepsApp.shortName}` : ""}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 360, pt: 1 }}>
					<BodyText>Add {stepsApp?.shortName} to your home screen for a faster, full-screen app.</BodyText>
					{iosSteps.map((step) => (
						<Box key={step.text} sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
							<Icon sx={{ color: "primary.main" }}>{step.icon}</Icon>
							<BodyTextSmall>{step.text}</BodyTextSmall>
						</Box>
					))}
				</Box>
			</ModalDialog>
			<ModalDialog
				close={() => setIsFallbackOpen(false)}
				open={isFallbackOpen}
				showCloseButton
				title={t("pwaInstall.installTitle")}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 360, pt: 1 }}>
					<BodyText>{t("pwaInstall.installerUnavailable")}</BodyText>
				</Box>
			</ModalDialog>
		</Box>
	);
};

export default InstallAppList;
